import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/system";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";

/** Reliable canvas x/y for Chart.js ChartEvent or DOM event. */
function getPointerXY(event, chart) {
  if (
    event &&
    typeof event.x === "number" &&
    typeof event.y === "number" &&
    !Number.isNaN(event.x) &&
    !Number.isNaN(event.y)
  ) {
    return { x: event.x, y: event.y };
  }
  const raw = event?.native ?? event;
  const pos = getRelativePosition(raw, chart);
  if (pos && typeof pos.x === "number" && typeof pos.y === "number") {
    return { x: pos.x, y: pos.y };
  }
  return { x: NaN, y: NaN };
}

function getFirstVisibleBarDatasetMeta(chart) {
  const datasets = chart?.data?.datasets || [];
  for (let i = 0; i < datasets.length; i++) {
    const meta = chart.getDatasetMeta(i);
    if (!meta?.hidden && meta?.data?.length) return meta;
  }
  return chart?.getDatasetMeta?.(0);
}

/**
 * When the user clicks on an x-axis category label (below the plot), map the hit to a category index
 * so handlers receive the same shape as Chart.js bar elements: [{ index }].
 */
function getCategoryIndexFromXAxisLabelClick(event, chart) {
  const xScale = chart?.scales?.x;
  const labels = chart?.data?.labels;
  if (!xScale || !labels?.length) return -1;

  const { x, y } = getPointerXY(event, chart);
  if (Number.isNaN(x) || Number.isNaN(y)) return -1;

  const ca = chart.chartArea;
  if (!ca) return -1;

  // Labels sit below the plot; ignore clicks inside the bar area
  if (y < ca.bottom) return -1;

  // Click should be below plot and inside the lower canvas area near x-axis labels.
  // Some chart layouts (non-stacked, custom paddings) have tighter xScale bands, so keep this permissive.
  const bandTop = Math.max(ca.bottom, xScale.top);
  const bandBottom = Math.max(xScale.bottom, ca.bottom);
  const canvasBottom = chart.height ?? bandBottom;
  if (y < bandTop || y > canvasBottom) return -1;

  const n = labels.length;
  const barMeta = getFirstVisibleBarDatasetMeta(chart);

  const categoryCenterX = (i) => {
    const el = barMeta?.data?.[i];
    if (el && typeof el.x === "number" && !Number.isNaN(el.x)) {
      return el.x;
    }
    return xScale.getPixelForValue?.(i) ?? xScale.getPixelForTick?.(i);
  };

  let bestIdx = -1;
  let bestDist = Infinity;
  for (let i = 0; i < n; i++) {
    const px = categoryCenterX(i);
    if (px == null || Number.isNaN(px)) continue;
    const d = Math.abs(x - px);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  if (bestIdx < 0) return -1;

  let minSpacing = Infinity;
  for (let i = 1; i < n; i++) {
    const a = categoryCenterX(i - 1);
    const b = categoryCenterX(i);
    if (a == null || b == null) continue;
    const s = Math.abs(b - a);
    if (s > 0 && s < minSpacing) minSpacing = s;
  }
  if (!Number.isFinite(minSpacing) || minSpacing === Infinity) {
    minSpacing = Math.abs(xScale.right - xScale.left) / Math.max(n, 1);
  }

  const barEl = barMeta?.data?.[bestIdx];
  const halfBar =
    barEl && typeof barEl.width === "number" && barEl.width > 0
      ? barEl.width / 2 + 16
      : minSpacing / 2 + 20;

  return bestDist <= halfBar ? bestIdx : -1;
}

// Custom plugin to handle zero values and minimum bar heights
const barHeightPlugin = {
  id: "barHeightPlugin",
  beforeDatasetDraw(chart, args) {
    const { ctx } = chart;
    const meta = args.meta;
    meta.data.forEach((bar, index) => {
      const value = bar.$context.raw;
      if (value === 0 || value === '0') {
        bar.height = 0;
      }
    });
  }
};

// Register Chart.js modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

export const CustomBarChart = ({
  titleTextSize = "24px",
  boxRadius = "24px",
  title,
  subTitle,
  titleStyle = {},
  subTitleStyle = {},
  breadcrumbs = [],
  labels = [],
  datasets = [],
  legendItems = [],
  xAxisTitle,
  /** Fires only when clicking a bar (dataset element), not the x-axis labels. */
  onBarClick,
  /** Fires when clicking an x-axis category label; use this for drill-down / next graph. */
  onXAxisCategoryClick,
  /** Common drill-down handler that can be reused for bar and x-axis clicks. */
  onCategoryClick,
  /** Toggle bar click interaction when using onCategoryClick. */
  enableBarClick = true,
  /** Toggle x-axis label click interaction when using onCategoryClick. */
  enableXAxisCategoryClick = true,
  hoverCursor,
  topRightAction,
  stacked = false,
  yAxisMax = 2000,
  yAxisMin = 0,
  yAxisStepSize = 500,
  yAxisFormatter,
  yAxisTitle = "tyres",
  datalabelsFormatter,
  barThickness,
  barPercentage = 0.6,
  categoryPercentage = 0.5,
  hideTotalOnTop = false,
  noBorder = false,
  noShadow = false,
  boxPadding = { xs: 4, md: 6 },
  boxBorder = "1px solid #E9DFFA",
  chartHeight = "500px",
  enableHorizontalScroll = false,
  minCategoryWidth = 90,
  maxVisibleCategories = 5,
  yAxisTitlePosition = "-top-[25px]", // Added for custom positioning
  breadcrumbStyle = {},
  headerLayout = "row", // "row" or "stack"
  interactiveTooltip = false,
  allowBarClick = true,
}) => {
  const chartRef = useRef(null);
  const chartViewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [chartAreaMetrics, setChartAreaMetrics] = useState({ top: 0, bottom: 0 });
  const chartAreaMetricsRef = useRef({ top: 0, bottom: 0 });

  const resolvedBarClickHandler = onBarClick || (enableBarClick ? onCategoryClick : undefined);
  const resolvedXAxisClickHandler =
    onXAxisCategoryClick || (enableXAxisCategoryClick ? onCategoryClick : undefined);
  const xAxisClickHandlerRef = useRef(resolvedXAxisClickHandler);

  useEffect(() => {
    xAxisClickHandlerRef.current = resolvedXAxisClickHandler;
  }, [resolvedXAxisClickHandler]);

  const tooltipRef = useRef(null);
  const [tooltipEl, setTooltipEl] = useState(null);
  const hideTooltipTimeoutRef = useRef(null);
  const tooltipHoveredRef = useRef(false);
  const updateTooltipTimeoutRef = useRef(null);
  const pendingTooltipDataRef = useRef(null);

  const setTooltipRef = useCallback((node) => {
    if (node) {
      tooltipRef.current = node;
      setTooltipEl(node);
    }
  }, []);

  useEffect(() => {
    const el = tooltipEl;
    if (!el) return;

    const handleMouseEnter = () => {
      tooltipHoveredRef.current = true;
    };
    const handleMouseLeave = () => {
      tooltipHoveredRef.current = false;
      el.style.opacity = 0;
      el.style.pointerEvents = 'none';
    };
    const handleClick = (e) => {
      const target = e.target.closest('.tooltip-interactive-text');
      if (target && resolvedBarClickHandler) {
        const datasetIndex = parseInt(target.getAttribute('data-dataset'), 10);
        const dataIndex = parseInt(target.getAttribute('data-index'), 10);
        resolvedBarClickHandler(e, [{ index: dataIndex, datasetIndex: datasetIndex }]);
      }
    };

    el.addEventListener('mouseenter', handleMouseEnter);
    el.addEventListener('mouseleave', handleMouseLeave);
    el.addEventListener('click', handleClick);

    return () => {
      el.removeEventListener('mouseenter', handleMouseEnter);
      el.removeEventListener('mouseleave', handleMouseLeave);
      el.removeEventListener('click', handleClick);
      if (hideTooltipTimeoutRef.current) clearTimeout(hideTooltipTimeoutRef.current);
    };
  }, [resolvedBarClickHandler, tooltipEl]);

  useEffect(() => {
    if (!enableHorizontalScroll) return;
    const viewportNode = chartViewportRef.current;
    if (!viewportNode) return;

    const updateWidth = () => {
      setViewportWidth(viewportNode.clientWidth || 0);
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        updateWidth();
      });
    });
    resizeObserver.observe(viewportNode);

    return () => {
      resizeObserver.disconnect();
    };
  }, [enableHorizontalScroll]);

  /**
   * Chart.js only calls options.onClick when the pointer is inside chart.chartArea (the plot).
   * X-axis labels are drawn below that rectangle, so label clicks never reached our handler.
   * afterEvent runs for all canvas events; we handle category clicks when the point is NOT in chartArea.
   */
  const xAxisCategoryPlugin = useMemo(
    () => ({
      id: "xAxisCategoryInteraction",
      afterEvent(chart, args) {
        const { event, replay } = args;
        const latestXAxisClickHandler = xAxisClickHandlerRef.current;
        if (replay || !event?.native || !latestXAxisClickHandler) return;

        const nativeType = event.native.type;
        if (nativeType === "click") {
          if (chart.isPointInArea(event)) return;
          const idx = getCategoryIndexFromXAxisLabelClick(event, chart);
          const labelLen = chart.data?.labels?.length ?? 0;
          const dataLen = chart.data?.datasets?.[0]?.data?.length ?? labelLen;
          const cap = Math.min(labelLen, dataLen);
          if (idx >= 0 && idx < cap) {
            // Deferred: the drilldown handler unmounts this chart (loading
            // spinner) and React 17 flushes that synchronously — destroying
            // the chart mid-event makes Chart.js render with a null ctx.
            setTimeout(() => latestXAxisClickHandler(event, [{ index: idx }]), 0);
          }
          return;
        }

        if (nativeType === "mousemove" && chart.canvas) {
          if (chart.isPointInArea(event)) return;
          const idx = getCategoryIndexFromXAxisLabelClick(event, chart);
          const labelLen = chart.data?.labels?.length ?? 0;
          const dataLen = chart.data?.datasets?.[0]?.data?.length ?? labelLen;
          const cap = Math.min(labelLen, dataLen);
          chart.canvas.style.cursor = idx >= 0 && idx < cap ? "pointer" : "default";
        }
      },
    }),
    [],
  );

  const data = {
    labels: labels || [],
    datasets: (datasets || []).map((ds) => {
      const originalBg = ds.backgroundColor;
      const originalBorder = ds.borderColor || ds.backgroundColor;
      return {
        ...ds,
        barPercentage: ds.barPercentage ?? barPercentage,
        categoryPercentage: ds.categoryPercentage ?? categoryPercentage,
        barThickness: ds.barThickness ?? barThickness,
        minBarLength: ds.minBarLength ?? 0,
        backgroundColor: (ctx) => {
          const val = ctx.dataset.data[ctx.dataIndex];
          if (val === 0 || val === '0') return 'transparent';
          if (Array.isArray(originalBg)) return originalBg[ctx.dataIndex];
          return originalBg;
        },
        borderColor: (ctx) => {
          const val = ctx.dataset.data[ctx.dataIndex];
          if (val === 0 || val === '0') return 'transparent';
          if (Array.isArray(originalBorder)) return originalBorder[ctx.dataIndex];
          return originalBorder;
        },
        hoverBackgroundColor: (ctx) => {
          const val = ctx.dataset.data[ctx.dataIndex];
          if (val === 0 || val === '0') return 'transparent';
          if (ds.hoverBackgroundColor) return ds.hoverBackgroundColor;
          if (Array.isArray(originalBg)) return originalBg[ctx.dataIndex];
          return originalBg;
        }
      };
    }),
  };

  const labelCount = labels?.length || 0;
  const barsVisibleAtOnce = Math.max(1, maxVisibleCategories);
  const computedCategoryWidth = enableHorizontalScroll && viewportWidth
    ? Math.max(minCategoryWidth, viewportWidth / barsVisibleAtOnce)
    : minCategoryWidth;
  const resolvedMinChartWidth = enableHorizontalScroll
    ? labelCount * computedCategoryWidth
    : Math.max(720, labelCount * computedCategoryWidth);

  /** Y tick marks for fixed rail when chart body scrolls horizontally (Chart.js y-axis is hidden). */
  const fixedYAxisTicks = useMemo(() => {
    if (!enableHorizontalScroll) return [];
    const min = Number(yAxisMin) ?? 0;
    const max = Number(yAxisMax) ?? 0;
    const step = Number(yAxisStepSize) || 1;
    if (!Number.isFinite(step) || step <= 0) return [min, max];
    const ticks = [];
    const epsilon = step * 1e-9;
    for (let v = min; v <= max + epsilon; v += step) {
      const rounded = Math.round(v * 1e6) / 1e6;
      ticks.push(rounded);
    }
    return ticks;
  }, [enableHorizontalScroll, yAxisMin, yAxisMax, yAxisStepSize]);

  const yValueSpan = Math.max(Number(yAxisMax) - Number(yAxisMin), 1e-9);
  const tickTopPercent = (value) =>
    ((Number(yAxisMax) - Number(value)) / yValueSpan) * 100;

  const formatYTick = (v) =>
    yAxisFormatter && typeof yAxisFormatter === "function"
      ? yAxisFormatter(v)
      : v;

  const chartAreaSyncPlugin = useMemo(
    () => ({
      id: "chartAreaSync",
      afterLayout(chart) {
        if (!enableHorizontalScroll) return;
        const area = chart?.chartArea;
        if (!area) return;
        const next = { top: area.top, bottom: area.bottom };
        const prev = chartAreaMetricsRef.current;
        if (
          Math.abs(prev.top - next.top) > 0.5 ||
          Math.abs(prev.bottom - next.bottom) > 0.5
        ) {
          chartAreaMetricsRef.current = next;
          setChartAreaMetrics(next);
        }
      },
    }),
    [enableHorizontalScroll],
  );

  const tickTopPx = (value) => {
    const areaHeight = chartAreaMetrics.bottom - chartAreaMetrics.top;
    if (!enableHorizontalScroll || areaHeight <= 0) {
      return null;
    }
    return (
      chartAreaMetrics.top +
      ((Number(yAxisMax) - Number(value)) / yValueSpan) * areaHeight
    );
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    onClick: (event, elements) => {
      if (allowBarClick && elements?.length > 0 && resolvedBarClickHandler) {
        // Deferred: see xAxisCategoryPlugin — a synchronous unmount here
        // crashes Chart.js' in-flight event render.
        const elementsSnapshot = [...elements];
        setTimeout(() => resolvedBarClickHandler(event, elementsSnapshot), 0);
      }
    },
    onHover: (event, elements, chart) => {
      const target = event.native?.target;
      if (!target) return;
      const onBar = elements?.length > 0;
      const onLabel = !!chart.hoveredDatalabel;
      const showPointer = (onBar && (resolvedBarClickHandler || hoverCursor)) || onLabel;
      target.style.cursor = showPointer ? "pointer" : "default";
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        filter: (tooltipItem) => {
          const v = tooltipItem.parsed?.y;
          return v != null && Number(v) !== 0;
        },
        external: (context) => {
          const { chart, tooltip } = context;
          const tooltipEl = tooltipRef.current;
          if (!tooltipEl) return;

          // Hide if no tooltip, opacity is 0, or every item is zero (filter may be incomplete for external)
          const pts = tooltip?.dataPoints?.filter(
            (dp) => dp.parsed?.y != null && Number(dp.parsed.y) !== 0
          );
          if (!tooltip || tooltip.opacity === 0 || !pts?.length) {
            if (updateTooltipTimeoutRef.current) {
              clearTimeout(updateTooltipTimeoutRef.current);
              updateTooltipTimeoutRef.current = null;
            }
            if (!hideTooltipTimeoutRef.current) {
              hideTooltipTimeoutRef.current = setTimeout(() => {
                if (!tooltipHoveredRef.current) {
                  tooltipEl.style.opacity = 0;
                  tooltipEl.style.pointerEvents = 'none';
                }
                hideTooltipTimeoutRef.current = null;
              }, 600);
            }
            return;
          }

          if (hideTooltipTimeoutRef.current) {
            clearTimeout(hideTooltipTimeoutRef.current);
            hideTooltipTimeoutRef.current = null;
          }

          if (tooltipHoveredRef.current) return;

          if (updateTooltipTimeoutRef.current) {
            clearTimeout(updateTooltipTimeoutRef.current);
          }

          pendingTooltipDataRef.current = context;

          const renderTooltip = (ctx) => {
            const { tooltip: t } = ctx;
            const titleLines = t.title || [];
            const titleText = titleLines.join(' ').replace(/,/g, " ");
            let innerHtml = '';
            if (titleText) {
                innerHtml += `<div style="font-size: 14px; font-weight: bold; margin-bottom: 4px; font-family: 'Inter', sans-serif; pointer-events: none;">${titleText}</div>`;
            }

            innerHtml += `<div>`;
            (t.dataPoints || [])
              .map((dp, idx) => ({ dp, idx }))
              .filter(({ dp }) => dp.parsed?.y != null && Number(dp.parsed.y) !== 0)
              .forEach(({ dp, idx: origIdx }) => {
                let labelLabel = dp.dataset.label || '';
                if (labelLabel) labelLabel += ": ";
                let value = dp.parsed.y;

                if (yAxisFormatter && typeof yAxisFormatter === "function") {
                    const sample = yAxisFormatter(value).toString();
                    if (sample.includes('%')) {
                        labelLabel += value.toFixed(2) + "%";
                    } else {
                        labelLabel += yAxisFormatter(value).toString().replace(/,/g, "");
                    }
                } else {
                    labelLabel += value !== null ? value.toString().replace(/,/g, "") : "";
                }

                if (dp.dataset.counts && dp.dataset.counts[dp.dataIndex] !== undefined) {
                    const rawCount = dp.dataset.counts[dp.dataIndex].toString().replace(/,/g, "");
                    labelLabel += " (" + rawCount + ")";
                }

                const color = (t.labelColors && t.labelColors[origIdx])
                  ? t.labelColors[origIdx].backgroundColor
                  : '#000';
                
                // Add an invisible hitbox extension to the tooltip text to make it easier to reach
                innerHtml += `
                  <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 2px; font-family: 'Inter', sans-serif; position: relative;">
                     <span style="display: inline-block; width: 12px; height: 12px; background-color: ${color}; border-radius: 2px; pointer-events: none;"></span>
                     <span 
                       class="tooltip-interactive-text" 
                       data-dataset="${dp.datasetIndex}" 
                       data-index="${dp.dataIndex}"
                       style="font-size: 12px; cursor: pointer; color: white; text-decoration: none; white-space: nowrap; position: relative;"
                       onmouseover="this.style.textDecoration='underline'"
                       onmouseout="this.style.textDecoration='none'"
                     >
                       ${labelLabel}
                       <div style="position: absolute; top: -15px; left: -15px; right: -15px; bottom: -15px; z-index: -1;"></div>
                     </span>
                  </div>
                `;
            });
            innerHtml += `</div>`;

            // Caret positioning
            const yAlign = t.yAlign || 'center';
            const xAlign = t.xAlign || 'center';
            const caretX = t.caretX;
            const caretY = t.caretY;

            let caretStyle = '';
            if (yAlign === 'bottom') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-top-color: rgba(0,0,0,0.8); border-width: 6px 6px 0 6px; bottom: -6px; left: 50%; transform: translateX(-50%);`;
            } else if (yAlign === 'top') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-bottom-color: rgba(0,0,0,0.8); border-width: 0 6px 6px 6px; top: -6px; left: 50%; transform: translateX(-50%);`;
            } else if (xAlign === 'left') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-right-color: rgba(0,0,0,0.8); border-width: 6px 6px 6px 0; left: -6px; top: 50%; transform: translateY(-50%);`;
            } else if (xAlign === 'right') {
                caretStyle = `position: absolute; width: 0; height: 0; border-style: solid; border-color: transparent; border-left-color: rgba(0,0,0,0.8); border-width: 6px 0 6px 6px; right: -6px; top: 50%; transform: translateY(-50%);`;
            }
            if (caretStyle) {
                innerHtml += `<div style="${caretStyle} pointer-events: none;"></div>`;
            }

            tooltipEl.innerHTML = innerHtml;
            tooltipEl.style.opacity = 1;
            tooltipEl.style.pointerEvents = 'auto';
            
            tooltipEl.style.left = caretX + 'px';
            tooltipEl.style.top = caretY + 'px';
            
            let translateX = '-50%';
            let translateY = '0';
            let offsetX = 0;
            let offsetY = 0;

            if (yAlign === 'bottom') { translateY = '-100%'; offsetY = -12; }
            else if (yAlign === 'top') { translateY = '0'; offsetY = 12; }
            else { translateY = '-50%'; }

            if (xAlign === 'left') { translateX = '0'; offsetX = 12; }
            else if (xAlign === 'right') { translateX = '-100%'; offsetX = -12; }
            else { translateX = '-50%'; }

            tooltipEl.style.transform = `translate(${translateX}, ${translateY}) translate(${offsetX}px, ${offsetY}px)`;
          };

          const isVisible = tooltipEl.style.opacity === '1';
          if (!isVisible) {
            renderTooltip(context);
          } else {
            updateTooltipTimeoutRef.current = setTimeout(() => {
              if (tooltipHoveredRef.current) return;
              if (pendingTooltipDataRef.current) {
                renderTooltip(pendingTooltipDataRef.current);
              }
              updateTooltipTimeoutRef.current = null;
            }, 250);
          }
        },
      },
      datalabels: {
        display: function (context) {
          const raw = context.dataset.data[context.dataIndex];
          if (raw === null || raw === undefined) return false;
          if (raw === "" || raw === "0" || raw === 0) return false;
          const n = Number(raw);
          return Number.isFinite(n) && n !== 0;
        },
        formatter: (value, context) => {
          if (typeof datalabelsFormatter === 'function') return datalabelsFormatter(value, context);
          return typeof value === 'number' ? Math.round(value) : (value?.toString() || "");
        },
        anchor: stacked ? "center" : "end",
        align: stacked ? "center" : "end",
        textAlign: "center", // Align multiline text centrally within its bounding box
        color: "#000000",
        font: {
          family: "'Inter', sans-serif",
          size: 11,
          weight: "600",
        },
        padding: 4,
        listeners: {
          enter: (context) => {
            if (resolvedBarClickHandler) {
              context.chart.hoveredDatalabel = { datasetIndex: context.datasetIndex, index: context.dataIndex };
              context.chart.canvas.style.cursor = 'pointer';
              context.chart.draw(); 
            }
          },
          leave: (context) => {
            context.chart.hoveredDatalabel = null;
            context.chart.canvas.style.cursor = 'default';
            context.chart.draw();
          },
          click: (context) => {
            if (resolvedBarClickHandler) {
              // Deferred: see xAxisCategoryPlugin — a synchronous unmount here
              // crashes Chart.js' in-flight event render.
              const clicked = [{
                index: context.dataIndex,
                datasetIndex: context.datasetIndex
              }];
              setTimeout(() => resolvedBarClickHandler(context.event, clicked), 0);
            }
          }
        }
      },
    },
    layout: {
      padding: {
        top: 20,
      }
    },
    scales: {
      x: {
        stacked,
        grid: {
          display: false,
        },
        border: {
          display: true,
          color: "#E5E7EB",
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          padding: 8, // Added padding for x-axis labels
          color: "#000000",
        },
        title: {
          display: true,
          text: xAxisTitle,
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
          padding: { top: 12 }, // Requested: margin top 12px for Zones, Regions text
          color: "#000000",
        },
      },
      y: {
        stacked,
        display: !enableHorizontalScroll,
        min: yAxisMin,
        max: yAxisMax,
        ticks: {
          stepSize: yAxisStepSize,
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          color: "#000000",
          callback: function (value) {
            return yAxisFormatter ? yAxisFormatter(value) : value;
          },
        },
        grid: {
          display: false,
          color: "#F3F4F6",
          drawTicks: false,
        },
        border: {
          display: true,
          color: "#E5E7EB",
        },
      },
    },
  };

  const totalPlugin = {
    id: "totalPlugin",
    afterDatasetsDraw(chart) {
      if (!stacked || hideTotalOnTop) return;
      const { ctx, scales: { x, y } } = chart;
      chart.data.labels.forEach((_, i) => {
        let total = 0;
        let lastY = y.getPixelForValue(0);
        chart.data.datasets.forEach((dataset, j) => {
          const meta = chart.getDatasetMeta(j);
          if (!meta.hidden && dataset.data[i] !== undefined && dataset.data[i] !== null && dataset.data[i] !== 0) {
            total += Number(dataset.data[i]);
            lastY = Math.min(lastY, meta.data[i].y);
          }
        });
        if (total > 0 && lastY > 0) {
          ctx.save();
          ctx.font = "600 11px Inter, sans-serif";
          ctx.fillStyle = "#000000";
          ctx.textAlign = "center";
          const formattedTotal = yAxisFormatter && yAxisFormatter(100).includes("%")
            ? total.toFixed(0) + "%"
            : total.toFixed(0);
          ctx.fillText(formattedTotal, x.getPixelForValue(i), lastY - 10);
          ctx.restore();
        }
      });
    },
  };

  const datalabelsUnderlinePlugin = {
    id: 'datalabelsUnderline',
    afterDatasetsDraw(chart) {
      if (!interactiveTooltip) return;
      const { ctx } = chart;
      chart.data.datasets.forEach((dataset, datasetIndex) => {
        const meta = chart.getDatasetMeta(datasetIndex);
        if (meta.hidden) return;
        meta.data.forEach((element, index) => {
          const hovered = chart.hoveredDatalabel;
          if (!hovered || 
              hovered.datasetIndex !== datasetIndex || 
              hovered.index !== index) return;

          const value = dataset.data[index];
          if (value === 0 || value === null || value === '0') return;
          
          // Determine text to underline
          let text = "";
          if (typeof datalabelsFormatter === 'function') {
            const context = {
              chart,
              dataIndex: index,
              datasetIndex: datasetIndex,
              dataset: dataset,
              active: true
            };
            const formatted = datalabelsFormatter(value, context);
            if (Array.isArray(formatted)) {
              text = String(formatted[0] ?? ""); // Underline the first line (count)
            } else {
              text = String(formatted ?? "");
            }
          } else {
            text = typeof value === 'number' ? Math.round(value).toString() : String(value ?? "");
          }
          
          ctx.save();
          // Use a very standard font string to ensure reliable measurement
          ctx.font = "bold 11px Inter, sans-serif";
          
          const textToMeasure = String(text || "");
          const textWidth = ctx.measureText(textToMeasure).width || 20;
          
          ctx.strokeStyle = "#000000";
          ctx.lineWidth = 1.5; // Thicker line for better visibility
          ctx.beginPath();
          
          let lineY;
          if (stacked) {
            const center = (element.y + element.base) / 2;
            lineY = center - 2.5; // Correctly positioned under the top line (count)
          } else {
            lineY = element.y - 5;
          }
          
          const startX = element.x - (textWidth / 2);
          const endX = element.x + (textWidth / 2);
          
          ctx.moveTo(startX, lineY);
          ctx.lineTo(endX, lineY);
          ctx.stroke();
          ctx.restore();
        });
      });
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "white",
        borderRadius: boxRadius,
        boxShadow: noShadow ? "none" : "0 4px 20px rgba(0,0,0,0.05)",
        border: noBorder ? "none" : boxBorder,
        p: noBorder ? 0 : boxPadding,
        position: "relative",
        paddingLeft:"16px"
      }}
    >
      {/* Dynamic Header Section */}
      {title !== null && (
        <div className={`flex flex-wrap ${headerLayout === "stack" ? "flex-col" : "flex-col lg:flex-row"} justify-between items-start mb-6 lg:mb-8 gap-4 px-2`}>
          <div className="flex-1 w-full">
            <div className="flex flex-row justify-between items-baseline gap-2 mb-4">
              <h2
                style={{
                  fontSize: titleTextSize,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 600,
                  lineHeight: "100%",
                  color: "#22C55E",
                  ...titleStyle
                }}
              >
                {title}
                {subTitle && (
                  <span
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: "18px",
                      fontWeight: 400,
                      fontStyle: "italic",
                      color: "black",
                      ...subTitleStyle
                    }}
                  >
                    {subTitle}
                  </span>
                )}
              </h2>

              {headerLayout === "stack" && legendItems && legendItems.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-3 rounded-[4px] px-2 py-1.5"
                  style={{ border: "0.5px solid #D9D9D9", background: "transparent" }}
                >
                  {legendItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <span
                        className="block w-3 h-3 rounded-sm"
                        style={{ backgroundColor: item.color }}
                      ></span>
                      <span className="text-[11px] font-medium text-black">
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Breadcrumbs and Actions for Stacked Layout */}
            {headerLayout === "stack" ? (
              <div className="flex flex-row justify-between items-center w-full min-h-[40px] gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
                  {breadcrumbs.map((crumb, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <span
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "13px",
                          lineHeight: "18px",
                          color: "#4841B9",
                          fontStyle: "italic",
                          fontWeight: crumb.active ? 600 : 400,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          ...breadcrumbStyle
                        }}
                        className={`transition-colors ${crumb.onClick ? "cursor-pointer hover:opacity-80" : ""
                          }`}
                        onClick={crumb.onClick}
                      >
                        {crumb.label}
                      </span>
                      {index < breadcrumbs.length - 1 && (
                        <span className="text-[#4841B9] font-medium">&gt;</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">

                  {topRightAction && <div className="flex-shrink-0">{topRightAction}</div>}
                </div>
              </div>
            ) : (
              /* Regular Inline Layout Breadcrumbs */
              breadcrumbs.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  {breadcrumbs.map((crumb, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <span
                        style={{
                          fontFamily: "Inter, sans-serif",
                          fontSize: "13px",
                          lineHeight: "18px",
                          color: "#4841B9",
                          fontStyle: "italic",
                          fontWeight: crumb.active ? 600 : 400,
                          textDecoration: "none",
                          whiteSpace: "nowrap",
                          ...breadcrumbStyle
                        }}
                        className={`transition-colors ${crumb.onClick ? "cursor-pointer hover:opacity-80" : ""
                          }`}
                        onClick={crumb.onClick}
                      >
                        {crumb.label}
                      </span>
                      {index < breadcrumbs.length - 1 && (
                        <span className="text-[#4841B9] font-medium">&gt;</span>
                      )}
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Top Right Actions and Legend for Row Layout */}
          {headerLayout !== "stack" && (
            <div className="flex flex-col items-end gap-3 w-full md:w-auto">
              <div className="flex items-center gap-4 w-full justify-end mt-[-7px]">
                {legendItems && legendItems.length > 0 && (
                  <div
                    className="flex flex-wrap items-center gap-4 rounded-[4px] px-2 py-2"
                    style={{ border: "0.5px solid #D9D9D9", background: "transparent" }}
                  >
                    {legendItems.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <span
                          className="block w-4 h-4 rounded-sm"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        <span className="text-[10px] font-medium text-black">
                          {item.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {topRightAction && <div className="flex-shrink-0">{topRightAction}</div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Chart Area with gap and Y-axis title adjustment */}
      <div
        className={`relative w-full mt-2 ${enableHorizontalScroll ? "flex min-w-0" : ""}`}
        style={{ height: chartHeight }}
      >
        {enableHorizontalScroll && (
          <div
            className="relative z-[2] flex w-[52px] flex-shrink-0 flex-col bg-white"
            aria-hidden
          >
            <div
              className="pointer-events-none absolute right-0 w-px bg-[#E5E7EB]"
              style={{
                top: `${chartAreaMetrics.top}px`,
                height: `${Math.max(chartAreaMetrics.bottom - chartAreaMetrics.top, 0)}px`,
              }}
            />
            <div
              className={`pointer-events-none absolute ${yAxisTitlePosition} right-[-36px] text-right text-[11px] leading-tight text-black`}
              style={{ fontFamily: "Inter, sans-serif", whiteSpace: "nowrap" }}
            >
              {yAxisTitle}
            </div>
            <div className="relative h-full">
              {fixedYAxisTicks.map((v) => (
                <span
                  key={v}
                  className="pointer-events-none absolute right-1 text-[11px] text-[#000000]"
                  style={{
                    top:
                      tickTopPx(v) != null
                        ? `${tickTopPx(v)}px`
                        : `${tickTopPercent(v)}%`,
                    transform: "translateY(-50%)",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {formatYTick(v)}
                </span>
              ))}
            </div>
          </div>
        )}

        {!enableHorizontalScroll && (
          <div
            className={`absolute ${yAxisTitlePosition} left-[48px] text-black pointer-events-none hidden md:block`}
            style={{
              fontSize: "12px",
              zIndex: 10,
              whiteSpace: "nowrap",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {yAxisTitle}
          </div>
        )}

        <div
          ref={chartViewportRef}
          className={
            enableHorizontalScroll
              ? "min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-hidden"
              : "h-full w-full"
          }
        >
            <div
              style={{
                width: enableHorizontalScroll ? `${resolvedMinChartWidth}px` : "100%",
                minWidth: enableHorizontalScroll ? `${resolvedMinChartWidth}px` : "100%",
                height: "100%",
                position: 'relative',
              }}
            >
              <Bar
                ref={chartRef}
                data={data}
                options={options}
                plugins={
                  stacked
                    ? [chartAreaSyncPlugin, xAxisCategoryPlugin, totalPlugin, barHeightPlugin, ChartDataLabels, datalabelsUnderlinePlugin]
                    : [chartAreaSyncPlugin, xAxisCategoryPlugin, barHeightPlugin, ChartDataLabels, datalabelsUnderlinePlugin]
                }
              />
              <div 
                ref={setTooltipRef}
                style={{
                  position: 'absolute',
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  borderRadius: '8px',
                  padding: '10px',
                  color: 'white',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.1s ease',
                  zIndex: 9999,
                  boxShadow: '0px 4px 6px rgba(0,0,0,0.1)',
                  whiteSpace: 'nowrap',
                }}
              />
            </div>
        </div>
      </div>
    </Box>
  );
};
